import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Redirect } from "react-router-dom";

import EmailUnsubscribeInfo from "@mapmycustomers/shared/types/email/EmailUnsubscribeInfo";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import { RootState } from "@app/store/rootReducer";

import styles from "./EmailUnsubscribe.module.scss";
import messages from "./messages";
import { loadEmailUnsubscribeInfo, unsubscribeEmail } from "./store/actions";
import { getEmailUnsubscribeInfo, isEmailUnsubscribing } from "./store/selectors";

interface EmailUnsubscribeProps {
  onLoadUnsubscribeInfo: typeof loadEmailUnsubscribeInfo.request;
  onUnsubscribe: typeof unsubscribeEmail.request;
  unsubscribeInfo?: EmailUnsubscribeInfo;
  unsubscribing: boolean;
}

const EmailUnsubscribe: React.FC<EmailUnsubscribeProps> = ({
  onLoadUnsubscribeInfo,
  onUnsubscribe,
  unsubscribeInfo,
  unsubscribing,
}) => {
  const intl = useIntl();
  const { search } = useLocation();

  const code = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return searchParams.get("id");
  }, [search]);

  useEffect(() => {
    if (!code) {
      return;
    }

    onLoadUnsubscribeInfo({ code });
  }, [code, onLoadUnsubscribeInfo]);

  const handleUnsubscribe = useCallback(() => {
    onUnsubscribe();
  }, [onUnsubscribe]);

  if (!code) {
    return <Redirect to={Path.ROOT} />;
  }

  if (!unsubscribeInfo) {
    return <LoadingSpinner global />;
  }

  return (
    <div className={styles.container}>
      {unsubscribeInfo.logoURL && (
        <img
          alt={unsubscribeInfo.teamName}
          className={styles.companyLogo}
          src={unsubscribeInfo.logoURL}
        />
      )}
      <span className={styles.text}>
        {unsubscribeInfo.subscribed
          ? intl.formatMessage(messages.unsubscribeText, { name: unsubscribeInfo.teamName })
          : intl.formatMessage(messages.unsubscribedText)}
      </span>
      {unsubscribeInfo.subscribed && (
        <Button
          className={styles.unsubscribeButton}
          loading={unsubscribing}
          onClick={handleUnsubscribe}
          type="primary"
        >
          {intl.formatMessage(messages.unsubscribeButtonLabel)}
        </Button>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  unsubscribeInfo: getEmailUnsubscribeInfo(state),
  unsubscribing: isEmailUnsubscribing(state),
});

const mapDispatchToProps = {
  onLoadUnsubscribeInfo: loadEmailUnsubscribeInfo.request,
  onUnsubscribe: unsubscribeEmail.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailUnsubscribe);
